// ============ HOOK PARA DETECTAR ESTADO DE CONEXIÓN ============
// client/src/lib/hooks/use-offline.ts

import { useState, useEffect, useCallback } from "react";
import { AuthService } from "@/lib/services/auth-offline"; // NUEVO: Para verificar capacidad offline

// NUEVO: Interfaz para el retorno del hook (tipado seguro)
export interface OfflineStatus {
  isOnline: boolean;
  isOffline: boolean;
  wasOffline: boolean;
  offlineCapable: boolean; // ¿Hay sesión guardada para operar sin internet?
  refreshCapability: () => Promise<void>; // Función para forzar re-chequeo de capacidad offline
}

export function useOffline() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  const [offlineCapable, setOfflineCapable] = useState(false); // ¿Hay sesión guardada para operar sin internet?
  const [username, setUsername] = useState<string | null>(null); // Usuario actual para verificar capacidad
  // COMENTARIO: Estos estados permiten saber si el usuario puede login offline, no solo si hay conexión

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setWasOffline(true);

      // Después de 3 segundos, resetear la bandera
      setTimeout(() => setWasOffline(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    // NUEVO: Escuchar cambios de autenticación para actualizar username y capacidad offline
    const handleAuthChange = (
      event: CustomEvent<{ username: string | null }>,
    ) => {
      const newUsername = event.detail?.username || null;
      setUsername(newUsername);

      // Re-evaluar capacidad offline inmediatamente
      if (newUsername && navigator.onLine) {
        AuthService.canLoginOffline(newUsername).then(setOfflineCapable);
      } else {
        setOfflineCapable(!!newUsername);
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener(
      "qva-auth-change",
      handleAuthChange as EventListener,
    ); // NUEVO LISTENER

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener(
        "qva-auth-change",
        handleAuthChange as EventListener,
      ); // CLEANUP
    };
  }, []); // ← El array de dependencias se mantiene vacío porque usamos funciones estables
  // COMENTARIO: El evento 'qva-auth-change' permite que AuthService.notifique cambios de sesión a toda la app

  // FUNCIÓN: Forzar re-chequeo de capacidad offline (útil tras login/logout)
  const refreshCapability = useCallback(async () => {
    if (username && navigator.onLine) {
      const canOffline = await AuthService.canLoginOffline(username);
      setOfflineCapable(canOffline);
    } else if (!navigator.onLine) {
      // Si está offline, asumimos que puede intentar login offline si tiene username
      setOfflineCapable(!!username);
    }
  }, [username]);
  // COMENTARIO: Esta función permite a los componentes forzar una actualización del estado offlineCapable

  return {
    isOnline,
    isOffline: !isOnline,
    wasOffline,
    offlineCapable, // CORRECCIÓN: Incluir para cumplir con OfflineStatus
    refreshCapability, // CORRECCIÓN: Incluir para permitir re-chequeo manual
  };
}
